import React, { useState, useEffect } from "react";

import PickDate from "./PickDate";
import PickContent from "./PickContent";
import PickDestinations from "./PickDestinations";
import { getPublishingProblems } from "../verification";
import CollectMailchimpDestinationData from "./mailchimp/CollectDestinationData";
import CollectYouTubeDestinationData from "./youtube/CollectYouTubeDestinationData";
import { getUser } from "../../../state/User";
import {
  closePopUp,
  showAlertPopUp,
  showLoadingPopUp,
  showPopUp,
} from "../../utils/Overlay";
import { newJob } from "../../../apis/firebase";
import { DestinationType } from "../../../constants";

export default function Scheduler({
  campaign,
  jobsManager,
}: {
  campaign: Post;
  jobsManager: JobsManager;
}) {
  const user = getUser();
  const [schedulerPhaseIdx, setSchedulerPhase] = useState(1);
  const destinations = user ? user.destinations || [] : [];
  const [date, setDate] = useState(new Date());
  const [content, setContent] = useState([]);
  const [mapping, setMapping] = useState<SchedulerMapping[]>([]);
  const [verificationResult, setVerificationResult] = useState<number[]>([
    0,
    0,
  ]);

  useEffect(() => {
    async function verify() {
      if (mapping) {
        const results = await Promise.all(
          mapping.map((item) =>
            getPublishingProblems(
              destinations.find(
                (d) => d.id === item.destinationId
              ) as Destination,
              item.content
                .map((id) => campaign.content?.find((c) => c.id === id))
                .filter(Boolean) as Content[]
            )
          )
        );
        const [problems, warnings] = results.reduce(
          (res, [problems, warnings]) => {
            res[0] += problems.length;
            res[1] += warnings.length;
            return res;
          },
          [0, 0]
        );
        setVerificationResult([problems, warnings]);
      }
    }
    verify();
  }, [JSON.stringify(mapping)]);

  const isDateOk = date !== null;
  const isContentOk = content.length > 0;
  const isMappingOk = mapping && mapping.length > 0;

  const isPublishingOk =
    verificationResult[0] === 0 && isDateOk && isContentOk && isMappingOk;

  return (
    <div className="scheduler p1 mb1">
      <div className="layout scheduler">
        <PickContent
          onSelect={setContent}
          campaign={campaign}
          key={`c_${schedulerPhaseIdx}}`}
        />
        {isContentOk && (
          <PickDestinations
            onSelect={setMapping}
            content={content}
            campaign={campaign}
            key={"destinations" + schedulerPhaseIdx}
          />
        )}
        {isMappingOk && (
          <PickDate onSelect={setDate} key={"date" + schedulerPhaseIdx} />
        )}
        {isPublishingOk && (
          <div className="scheduler-phase tac">
            <button
              className="primary mt2"
              disabled={!isPublishingOk}
              onClick={() => {
                setJobs(campaign, mapping, destinations, date).then(() => {
                  setDate(new Date());
                  setContent([]);
                  setMapping([]);
                  setVerificationResult([0, 0]);
                  setSchedulerPhase(0);
                  jobsManager[1]();
                  closePopUp();
                });
              }}
            >
              Publish
            </button>
            <small className="block mt1 tac">
              {schedulingProcessCheck(
                isDateOk,
                isContentOk,
                isMappingOk,
                verificationResult
              )}
            </small>
          </div>
        )}
      </div>
    </div>
  );
}

function schedulingProcessCheck(
  isDateOk: boolean,
  isContentOk: boolean,
  isMappingOk: boolean,
  verificationResult: number[]
) {
  let msg = "";
  if (!isDateOk) {
    msg = `Please, pick a date and time of your post.`;
  } else if (!isContentOk) {
    msg = `Please, pick content for your post.`;
  } else if (!isMappingOk) {
    msg = `Select which content goes where.`;
  } else {
    const [problems, warnings] = verificationResult;
    function getProblems() {
      return `${problems} problem${problems !== 1 ? "s" : ""}`;
    }
    function getWarnings() {
      return `${warnings} warning${warnings !== 1 ? "s" : ""}`;
    }
    if (problems > 0 && warnings > 0) {
      msg = `You have ${getProblems()} to solve before publishing. You also have{" "}
        ${getWarnings()}.
      `;
    } else if (problems > 0) {
      msg = `You have ${getProblems()} to solve before publishing.`;
    } else if (warnings > 0) {
      msg = `You have ${getWarnings()}. Check ${
        warnings === 1 ? "it" : "them"
      } before publishing.`;
    }
  }
  return msg !== "" ? <small className="tac mb2">{msg}</small> : null;
}

async function setJobs(
  campaign: Post,
  mapping: SchedulerMapping[],
  destinations: Destination[],
  date: Date
) {
  const jobs: any[] = [];
  for (let i = 0; i < mapping.length; i++) {
    const item = mapping[i];
    const destination = destinations.find((d) => d.id === item.destinationId);
    if (destination) {
      // **************************************************************** mailchimp
      if (destination.type === DestinationType.MAILCHIMP) {
        let meta = await new Promise((done) => {
          showPopUp(
            <CollectMailchimpDestinationData
              destination={destination}
              onCollect={(destination, subject, fromName, fromEmail) => {
                done({
                  subject,
                  fromName,
                  fromEmail,
                });
              }}
              onCancel={(destination) => {
                console.log("Publishing canceled");
              }}
            />,
            { width: "500px" }
          );
        });
        if (meta) {
          jobs.push({
            content: item.content,
            destinationId: item.destinationId,
            destinationMeta: meta,
            date,
          });
        }
        // **************************************************************** youtube
      } else if (destination.type === DestinationType.YOUTUBE) {
        let meta = await new Promise((done) => {
          showPopUp(
            <CollectYouTubeDestinationData
              destination={destination}
              onCollect={(destination, title) => {
                done({
                  title,
                });
              }}
              onCancel={(destination) => {
                console.log("Publishing canceled");
              }}
            />,
            { width: "500px" }
          );
        });
        if (meta) {
          jobs.push({
            content: item.content,
            destinationId: item.destinationId,
            destinationMeta: meta,
            date,
          });
        }
      } else {
        jobs.push({
          content: item.content,
          destinationId: item.destinationId,
          destinationMeta: destination.meta || {},
          date,
        });
      }
    }
  }

  showLoadingPopUp("Preparing posts ...");
  try {
    for (let i = 0; i < jobs.length; i++) {
      const job = jobs[i];
      await newJob(
        campaign.id,
        job.content,
        job.destinationId,
        // @ts-ignore
        job.destinationMeta,
        job.date
      );
    }
  } catch (err) {
    console.error(err);
    showAlertPopUp(
      <p>There is a problem saving your task. Please try again later.</p>
    );
  }
  closePopUp();
}
